import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaHome,
  FaWallet,
  FaMicrophone,
  FaBuilding,
  FaUsers,
  FaBars,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { appColors, appShadows } from "../../utils/appTheme";

/**
 * Menú radial bajo el pulgar — la navegación estrella de la app.
 * Port fiel del prototipo (design_handoff_habitia_app_radial, JS 464-481, CSS 163-181).
 *
 * Se monta solo en mobile (el shell decide). Reemplaza a BottomNav y al FAB del micrófono.
 * - pointerdown en la thumbzone (bottom 150px) abre el semicírculo en el punto tocado.
 * - move resalta el ítem cuyo ángulo de arco esté a ±20° con distancia > 44px.
 * - up sobre un ítem navega; tap sin drag (<6px) deja el menú sticky (ítems clickeables).
 * - tap/click/Enter en el FAB "Menú" abre el menú sticky centrado (fallback teclado/mouse).
 */

const MENU = [
  { key: "inicio", label: "Inicio", icon: <FaHome /> },
  { key: "finanzas", label: "Finanzas", icon: <FaWallet /> },
  { key: "habi", label: "Habi", icon: <FaMicrophone /> },
  { key: "edificio", label: "Edificio", icon: <FaBuilding /> },
  { key: "prestadores", label: "Proveedores", icon: <FaUsers /> },
];

const R = 116;
const A0 = 158;
const A1 = 22;

const RadialMenu = ({ onNavigate, items = MENU }) => {
  const { t } = useTranslation("nav");
  const N = items.length;
  const [open, setOpen] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const [activeIndex, setActiveIndex] = useState(-1);

  const startRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const originRef = useRef({ x: 0, y: 0 });
  const activeRef = useRef(-1);
  const rafRef = useRef(0);
  const pointRef = useRef({ x: 0, y: 0 });
  const navRef = useRef(onNavigate);

  useEffect(() => {
    navRef.current = onNavigate;
  }, [onNavigate]);

  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Posiciones e ítem-ángulos precomputados (idénticos al prototipo).
  const positions = useMemo(
    () =>
      items.map((_, i) => {
        const ia = A0 + (A1 - A0) * (N > 1 ? i / (N - 1) : 0.5);
        const a = (ia * Math.PI) / 180;
        return { dx: Math.cos(a) * R, dy: -Math.sin(a) * R, ia };
      }),
    [items, N]
  );

  const clampOrigin = (x, y) => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const minY = Math.min(360, H - 70);
    return {
      x: Math.max(70, Math.min(W - 70, x)),
      y: Math.max(minY, Math.min(H - 70, y)),
    };
  };

  const computeActive = useCallback(
    (lx, ly) => {
      const { x: ox, y: oy } = originRef.current;
      const dx = lx - ox;
      const dy = ly - oy;
      const dist = Math.hypot(dx, dy);
      const ang = (Math.atan2(-dy, dx) * 180) / Math.PI;
      let best = -1;
      let bestd = 20;
      if (dist > 44) {
        positions.forEach((p, i) => {
          const d = Math.abs(((p.ia - ang + 540) % 360) - 180);
          if (d < bestd) {
            bestd = d;
            best = i;
          }
        });
      }
      return best;
    },
    [positions]
  );

  const close = useCallback(() => {
    setOpen(false);
    setSticky(false);
    setActiveIndex(-1);
    activeRef.current = -1;
  }, []);

  const navigate = useCallback((key) => {
    if (navRef.current) navRef.current(key);
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      pointRef.current = { x: e.clientX, y: e.clientY };
      if (Math.hypot(e.clientX - startRef.current.x, e.clientY - startRef.current.y) > 6) {
        movedRef.current = true;
      }
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = 0;
          const best = computeActive(pointRef.current.x, pointRef.current.y);
          if (best !== activeRef.current) {
            activeRef.current = best;
            setActiveIndex(best);
          }
        });
      }
    },
    [computeActive]
  );

  const onPointerUp = useCallback(() => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const sel = activeRef.current;
    if (sel >= 0) {
      // Soltó sobre un ítem → navega y cierra.
      close();
      navigate(items[sel].key);
    } else {
      // Soltó sin ítem (tap o arrastre a vacío) → queda ABIERTO (sticky).
      // Se cierra tocando el scrim o un ítem. Esto arregla el "no queda" en touch,
      // donde un tap suele registrar >6px de movimiento.
      setSticky(true);
    }
  }, [close, navigate, onPointerMove, items]);

  const onPointerDown = useCallback(
    (e) => {
      const x = e.clientX;
      const y = e.clientY;
      startRef.current = { x, y };
      movedRef.current = false;
      const o = clampOrigin(x, y);
      originRef.current = o;
      setOrigin(o);
      setActiveIndex(-1);
      activeRef.current = -1;
      setSticky(false);
      setOpen(true);
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      e.preventDefault();
    },
    [onPointerMove, onPointerUp]
  );

  const openStickyCentered = useCallback(() => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const o = clampOrigin(W / 2, H - 160);
    originRef.current = o;
    setOrigin(o);
    setActiveIndex(-1);
    activeRef.current = -1;
    setOpen(true);
    setSticky(true);
  }, []);

  // Limpieza + Escape para cerrar.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [close, onPointerMove, onPointerUp]);

  if (typeof document === "undefined") return null;

  const itemTransition = (i) =>
    `transform .28s cubic-bezier(.2,1.3,.4,1) ${reduceMotion ? 0 : i * 22}ms, background .15s ease, box-shadow .15s ease`;

  return createPortal(
    <>
      {/* Scrim */}
      {open && (
        <div
          onClick={sticky ? close : undefined}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,12,16,.32)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            zIndex: 90,
            transition: "opacity .2s ease",
          }}
        />
      )}

      {/* Radial abierto */}
      {open && (
        <div
          role="menu"
          aria-label="Navegación principal"
          style={{
            position: "fixed",
            left: origin.x,
            top: origin.y,
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: 20,
              height: 20,
              margin: "-10px 0 0 -10px",
              borderRadius: "50%",
              background: appColors.blue,
              boxShadow: "0 0 0 6px rgba(0,128,255,.18)",
            }}
          />
          {items.map((m, i) => {
            const p = positions[i];
            const active = i === activeIndex;
            return (
              <button
                key={m.key}
                type="button"
                role="menuitem"
                aria-label={t(`items.${m.key}.label`, m.label)}
                tabIndex={sticky ? 0 : -1}
                onClick={
                  sticky
                    ? () => {
                        close();
                        navigate(m.key);
                      }
                    : undefined
                }
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: 56,
                  height: 56,
                  margin: "-28px 0 0 -28px",
                  borderRadius: "50%",
                  border: "none",
                  background: active ? appColors.blue : appColors.surface,
                  color: active ? "#fff" : appColors.blue,
                  boxShadow: active ? appShadows.glow : appShadows.soft,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  cursor: sticky ? "pointer" : "default",
                  pointerEvents: sticky ? "auto" : "none",
                  transform: `translate(${p.dx}px,${p.dy}px) scale(${active ? 1.22 : 1})`,
                  transition: itemTransition(i),
                }}
              >
                {active && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 64,
                      left: "50%",
                      transform: "translateX(-50%)",
                      whiteSpace: "nowrap",
                      fontSize: 12,
                      background: appColors.ink,
                      color: appColors.page,
                      padding: "4px 9px",
                      borderRadius: 8,
                    }}
                  >
                    {t(`items.${m.key}.label`, m.label)}
                  </span>
                )}
                {m.icon}
              </button>
            );
          })}
        </div>
      )}

      {/* FAB "Menú" — único origen del gesto (tap abre; arrastrar despliega el
          radial). Ya NO hay franja invisible que robe toques al contenido. */}
      <button
        type="button"
        aria-label="Abrir menú"
        onPointerDown={onPointerDown}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openStickyCentered();
          }
        }}
        style={{
          position: "fixed",
          left: "50%",
          bottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
          transform: "translateX(-50%)",
          width: 58,
          height: 58,
          borderRadius: "50%",
          border: "none",
          background: appColors.blue,
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          boxShadow: appShadows.glow,
          cursor: "pointer",
          touchAction: "none",
          zIndex: 60,
          opacity: open ? 0 : 1,
          pointerEvents: open ? "none" : "auto",
          transition: "opacity .18s ease",
          animation: reduceMotion || open ? "none" : "appMenuPulse 2.4s ease-out infinite",
        }}
      >
        <FaBars style={{ fontSize: 19 }} />
        <span style={{ fontSize: 9, letterSpacing: ".04em" }}>Menú</span>
      </button>
    </>,
    document.body
  );
};

export default RadialMenu;
